import React from 'react';
import { Heart, Check, ExternalLink } from 'lucide-react';

const OfferCard = ({ offer, isSelected, onClick, isFavorite = false, onToggleFavorite, isViewed = false }) => {
    // Determine flag URL (using local assets)
    const countryCode = (offer?.countryCode && typeof offer.countryCode === 'string') ? offer.countryCode : 'FR';
    const flagUrl = `/assets/flags/no_background/${countryCode.toUpperCase()}.png`;

    return (
        <div
            onClick={() => onClick(offer)}
            className={`
                relative p-5 rounded-[20px] cursor-pointer transition-all duration-300 border group
                ${offer.isExpired ? 'opacity-60 grayscale-[0.4]' : ''}
                ${isSelected
                    ? 'bg-white border-blue-500 shadow-[0_4px_20px_rgba(59,130,246,0.15)] scale-[1.02]'
                    : 'bg-white border-slate-200 hover:border-gray-300 hover:shadow-lg scale-100'}
            `}
        >
            {/* "Déjà vu" / "Offre expirée" Badges */}
            {(isViewed || offer.isExpired) && (
                <div className="absolute top-3 left-3 z-20 flex gap-1.5">
                    {offer.isExpired && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-600 text-[10px] font-bold rounded">
                            Offre expirée
                        </span>
                    )}
                    {isViewed && (
                        <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-[10px] font-bold rounded">
                            Déjà vu
                        </span>
                    )}
                </div>
            )}
            {/* Header: Title, Flag, Heart */}
            <div className={`flex justify-between items-start mb-2 ${(isViewed || offer.isExpired) ? 'pt-5' : ''}`}>
                <div className="flex-1 pr-8">
                    <h3 className="font-extrabold text-[16px] text-[#1e293b] leading-tight mb-3">
                        {offer.title}
                    </h3>
                    <p className="text-[15px] font-semibold text-[#64748b]">
                        {offer.company}
                    </p>

                    {/* Tracking Checkboxes (Only in Tracking Mode) */}
                    {offer.trackingMode && (
                        <div className="mt-3 flex flex-col gap-2">
                            {/* Candidature envoyée */}
                            <div className="flex items-center gap-2">
                                <div
                                    className="relative flex items-center justify-center cursor-pointer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input type="checkbox" className="peer sr-only" id={`sent-${offer.id}`} />
                                    <label
                                        htmlFor={`sent-${offer.id}`}
                                        className="w-5 h-5 border-2 border-blue-500 rounded bg-white peer-checked:bg-blue-500 transition-all flex-shrink-0 cursor-pointer"
                                    />
                                    <Check className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200 pointer-events-none" strokeWidth={4} />
                                </div>
                                <span className="text-sm font-bold text-slate-700">Candidature envoyée</span>
                            </div>

                            {/* Candidature Trouve+ */}
                            <div className="flex items-center gap-2">
                                <div
                                    className="relative flex items-center justify-center cursor-pointer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input type="checkbox" className="peer sr-only" id={`trouve-${offer.id}`} />
                                    <label
                                        htmlFor={`trouve-${offer.id}`}
                                        className="w-5 h-5 border-2 border-blue-500 rounded bg-white peer-checked:bg-blue-500 transition-all flex-shrink-0 cursor-pointer"
                                    />
                                    <Check className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200 pointer-events-none" strokeWidth={4} />
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="text-sm font-bold text-slate-700">Candidature</span>
                                    {/* Increased size of Trouve+ logo to h-10 */}
                                    <img src="/assets/Trouve_plus.png" className="h-10 w-auto" alt="Trouve+" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex flex-col items-center gap-2 absolute right-5 top-3">
                    {/* Circular Flag */}
                    <div className="w-[32px] h-[32px] rounded-full overflow-hidden border border-gray-100 shadow-sm relative z-10">
                        <img
                            src={flagUrl}
                            alt={offer.country}
                            className={`w-full h-full object-cover transform ${(countryCode.toUpperCase() === 'DE' ? 'scale-[1.8]' :
                                countryCode.toUpperCase() === 'MX' ? 'scale-[1.7]' :
                                    countryCode.toUpperCase() === 'CH' ? 'scale-[1.6]' :
                                        'scale-[1.4]'
                            )}`}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://flagcdn.com/w80/${countryCode.toLowerCase()}.png`;
                                e.target.className = "w-full h-full object-cover scale-125";
                            }}
                        />
                    </div>
                    {/* Heart Icon */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleFavorite(offer.id);
                        }}
                        className="group mt-1 cursor-pointer"
                    >
                        {/* Only blue if isFavorite is true */}
                        <Heart
                            className={`w-5 h-5 transition-colors ${(isFavorite || offer.trackingMode) ? 'fill-blue-500 text-blue-500' : 'text-black fill-transparent group-hover:text-blue-500'}`}
                            strokeWidth={2}
                        />
                    </button>

                </div>
            </div>

            {/* Tags Row - Flattened to single line */}
            {/* Tags Row - Wraps if needed */}
            <div className="flex flex-wrap gap-2 mb-4 mt-3">
                <span className="px-3 py-1 rounded-full bg-[#f1f5f9] text-[#475569] text-xs font-bold border border-slate-200">
                    {offer.country}
                </span>
                <span className="px-3 py-1 rounded-full bg-[#f1f5f9] text-[#475569] text-xs font-bold border border-slate-200">
                    {offer.city}
                </span>
                <span className="px-3 py-1 rounded-full bg-[#e0f2fe] text-[#0284c7] text-xs font-bold border border-blue-100">
                    {offer.sector}
                </span>
                <span className="px-3 py-1 rounded-full bg-[#e0f2fe] text-[#0284c7] text-xs font-bold border border-blue-100">
                    {offer.duration}
                </span>
                {/* Language Label - Only show if languages exist and are not empty */}
                {offer.langues_requises && (() => {
                    const cleaned = offer.langues_requises.replace(/[\[\]"]/g, '').trim();
                    return cleaned ? (
                        <span className="px-3 py-1 rounded-full bg-purple-50 text-purple-600 text-xs font-bold border border-purple-100 truncate max-w-[150px]">
                            {cleaned.split(',').slice(0, 2).join(', ')}
                        </span>
                    ) : null;
                })()}
            </div>

            {/* Summary Box */}
            <div className={`
                p-3 rounded-xl text-sm leading-relaxed
                ${isSelected ? 'bg-slate-50 text-slate-600' : 'bg-[#f8fafc] text-slate-500'}
            `}>
                {(offer.summary || '').replace(/<\/?p>/gi, '').trim()}
            </div>
        </div>
    );
};

export default OfferCard;
